import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Link from '@cloudscape-design/components/link';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Alert from '@cloudscape-design/components/alert';

export const AboutSafeLinksTab = () => {
  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header variant="h2">What are Microsoft 365 Safe Links?</Header>
        }
      >
        <SpaceBetween size="m">
          <Box variant="p">
            Microsoft 365 Safe Links is a security feature within Office 365 Advanced Threat
            Protection (ATP) that provides time-of-click verification of URLs in email messages,
            Microsoft Teams, and Office documents. When Safe Links is enabled, URLs are rewritten
            to route through Microsoft's scanning service before reaching the final destination.
          </Box>
          <Box variant="p">
            This protection helps prevent users from accessing malicious websites by checking URLs
            against a continuously updated list of known malicious sites. The rewritten URLs follow
            a specific pattern that includes the original destination encoded as a parameter.
          </Box>
        </SpaceBetween>
      </Container>

      <Container
        header={
          <Header variant="h2">URL Structure</Header>
        }
      >
        <SpaceBetween size="m">
          <Box variant="h3">Safe Links Pattern</Box>
          <div
            style={{
              fontFamily: 'monospace',
              fontSize: '14px',
              padding: '12px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
              wordBreak: 'break-all',
            }}
          >
            https://[region].safelinks.protection.outlook.com/?url=[encoded-url]&data=[metadata]&sdata=[signature]&reserved=[flag]
          </div>

          <Box variant="h3">Components</Box>
          <SpaceBetween size="s">
            <div>
              <Box variant="strong">Domain</Box>
              <Box variant="p">
                The domain follows the pattern{' '}
                <span style={{ fontFamily: 'monospace' }}>
                  [region].safelinks.protection.outlook.com
                </span>{' '}
                where the region indicates the geographic location of the scanning service (e.g.,
                emea01, nam01, apac01).
              </Box>
            </div>

            <div>
              <Box variant="strong">url Parameter</Box>
              <Box variant="p">
                Contains the URL-encoded original destination URL. This is the primary parameter
                that holds the actual link the user intended to visit.
              </Box>
            </div>

            <div>
              <Box variant="strong">data Parameter</Box>
              <Box variant="p">
                Contains encoded metadata about the email, including user identifiers, timestamps,
                and other tracking information used for security analysis and reporting.
              </Box>
            </div>

            <div>
              <Box variant="strong">sdata Parameter</Box>
              <Box variant="p">
                Signature data used to validate the authenticity of the Safe Links URL and prevent
                tampering.
              </Box>
            </div>

            <div>
              <Box variant="strong">reserved Parameter</Box>
              <Box variant="p">
                A reserved flag used by Microsoft for internal purposes, typically set to 0.
              </Box>
            </div>
          </SpaceBetween>
        </SpaceBetween>
      </Container>

      <Container
        header={
          <Header variant="h2">Why Decode Safe Links?</Header>
        }
      >
        <SpaceBetween size="m">
          <Box variant="h3">Legitimate Use Cases</Box>
          <SpaceBetween size="s">
            <Box variant="p">
              • <strong>URL Verification:</strong> Confirm the actual destination before clicking,
              especially for important or sensitive links.
            </Box>
            <Box variant="p">
              • <strong>Link Sharing:</strong> Share clean URLs without the Safe Links wrapper for
              better readability and compatibility.
            </Box>
            <Box variant="p">
              • <strong>Automation:</strong> Process URLs in scripts or applications that don't
              support Safe Links redirection.
            </Box>
            <Box variant="p">
              • <strong>Documentation:</strong> Include clean URLs in documentation or reports
              instead of lengthy Safe Links URLs.
            </Box>
            <Box variant="p">
              • <strong>Troubleshooting:</strong> Diagnose issues with URLs that may be caused by
              the Safe Links wrapping.
            </Box>
          </SpaceBetween>
        </SpaceBetween>
      </Container>

      <Container
        header={
          <Header variant="h2">Security Considerations</Header>
        }
      >
        <SpaceBetween size="m">
          <Box variant="p">
            While this tool helps you decode Safe Links URLs, it's important to understand the
            security implications:
          </Box>

          <ExpandableSection headerText="Protection Benefits" defaultExpanded>
            <SpaceBetween size="s">
              <Box variant="p">
                Safe Links provides real-time protection by scanning URLs at the time of click,
                which means even if a link was safe when the email was sent, it will be checked
                again when you click it. This is crucial because websites can be compromised or
                links can be changed after an email is sent.
              </Box>
              <Box variant="p">
                The service also provides reporting and analytics to help administrators identify
                and respond to phishing attempts and other security threats targeting their
                organization.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          <ExpandableSection headerText="When to Use Caution">
            <SpaceBetween size="s">
              <Box variant="p">
                • Always verify that the decoded URL points to a legitimate and expected
                destination.
              </Box>
              <Box variant="p">
                • Be cautious of URLs with suspicious patterns, even after decoding (e.g., typos in
                domain names, unexpected redirects, unfamiliar domains).
              </Box>
              <Box variant="p">
                • Remember that bypassing Safe Links removes the real-time protection it provides.
              </Box>
              <Box variant="p">
                • Never share decoded URLs if you're unsure about their safety - let recipients
                benefit from Safe Links protection.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          <Alert type="warning">
            <strong>Important:</strong> Decoding a Safe Links URL removes Microsoft's protection
            layer. Always verify the destination is legitimate before accessing decoded URLs,
            especially if they're from unknown senders.
          </Alert>
        </SpaceBetween>
      </Container>

      <Container
        header={
          <Header variant="h2">Examples</Header>
        }
      >
        <SpaceBetween size="m">
          <Box variant="h3">Example Safe Links URL</Box>
          <div
            style={{
              fontFamily: 'monospace',
              fontSize: '12px',
              padding: '12px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
              wordBreak: 'break-all',
              marginBottom: '8px',
            }}
          >
            https://emea01.safelinks.protection.outlook.com/?url=https%3A%2F%2Fwww.strato.de%2Ffaq%2F&data=05%7C02%7C...
          </div>

          <Box variant="h3">Decoded Original URL</Box>
          <div
            style={{
              fontFamily: 'monospace',
              fontSize: '12px',
              padding: '12px',
              backgroundColor: '#e8f5e9',
              borderRadius: '4px',
              wordBreak: 'break-all',
            }}
          >
            https://www.strato.de/faq/
          </div>
        </SpaceBetween>
      </Container>

      <Container
        header={
          <Header variant="h2">Additional Resources</Header>
        }
      >
        <SpaceBetween size="s">
          <Box variant="p">
            Learn more about Microsoft 365 Safe Links and Advanced Threat Protection:
          </Box>
          <Box variant="p">
            •{' '}
            <Link
              href="https://learn.microsoft.com/en-us/microsoft-365/security/office-365-security/safe-links-about"
              external
            >
              Microsoft 365 Safe Links Documentation
            </Link>
          </Box>
          <Box variant="p">
            •{' '}
            <Link
              href="https://learn.microsoft.com/en-us/microsoft-365/security/office-365-security/defender-for-office-365"
              external
            >
              Microsoft Defender for Office 365
            </Link>
          </Box>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};

